import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminClientList } from '@/hooks/useAdmin'
import { useUIStore } from '@/store/ui'
import { AdminError } from './AdminError'
import { ClientFormModal } from './ClientFormModal'
import type { ClientSummary } from '@/hooks/useAdmin'


function ClientCard({ client, onEnter, onView }: {
  client: ClientSummary
  onEnter: () => void
  onView: () => void
}) {
  return (
    <div className={`client-card ${!client.active ? 'client-card--inactive' : ''}`}>
      <div className="client-card__header">
        <div>
          <h2 className="client-card__name">{client.display_name}</h2>
          <p className="client-card__location">{client.city_state}</p>
        </div>
        <span className={`active-badge ${client.active ? 'active-badge--on' : 'active-badge--off'}`}>
          {client.active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="client-card__stats">
        <div className="client-stat">
          <span className="client-stat__label">Seeds left</span>
          <span className="client-stat__value">
            {client.seedsRemaining === null
              ? 'no plan'
              : `${client.seedsRemaining} / ${client.seedsWeek ?? 0}`}
          </span>
        </div>
        <div className="client-stat">
          <span className="client-stat__label">Total stories</span>
          <span className="client-stat__value">{client.totalStories}</span>
        </div>
      </div>

      <div className="client-card__accent" style={{ background: client.accent_color }} />

      <div className="client-card__actions">
        <button className="btn-secondary" onClick={onView}>View details</button>
        <button className="btn-primary btn--amber" onClick={onEnter}>Enter portal →</button>
      </div>
    </div>
  )
}

export function AdminDashboard() {
  const { data: clients, isLoading, error } = useAdminClientList()
  const setViewingAs = useUIStore((s) => s.setViewingAs)
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  })

  if (isLoading) {
    return <div className="admin-loading">Loading clients…</div>
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">All Clients</h1>
          <p className="admin-page-sub">{today}</p>
        </div>
        <div className="admin-header-meta">
          <span className="admin-header-count">{clients?.length ?? 0} client{clients?.length !== 1 ? 's' : ''}</span>
          <button className="btn-primary btn--amber" onClick={() => setCreating(true)}>New client</button>
        </div>
      </div>

      <AdminError error={error} title="Could not load clients" />

      {creating && (
        <ClientFormModal
          onClose={() => setCreating(false)}
          onCreated={(client) => navigate(`/admin/client/${client.id}`)}
        />
      )}

      {!clients?.length ? (
        <div className="admin-empty">No clients yet.</div>
      ) : (
        <div className="client-grid">
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onView={() => navigate(`/admin/client/${client.id}`)}
              onEnter={() => {
                setViewingAs(client)
                navigate('/app')
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
